import { Language, ApiLanguage, SettingsTheme, ApiTheme } from "@/types";

// Language mapping between frontend codes and API names
export const LANGUAGE_MAP: Record<Language, ApiLanguage> = {
  en: "english",
  es: "spanish",
  fr: "french",
  de: "german",
  it: "italian",
};

// Reverse mapping for API to frontend
export const API_LANGUAGE_MAP: Record<ApiLanguage, Language> = {
  english: "en",
  spanish: "es",
  french: "fr",
  german: "de",
  italian: "it",
};

// Theme mapping between frontend and API
export const THEME_MAP: Record<SettingsTheme, ApiTheme> = {
  light: "light",
  dark: "dark",
  system: "device_default",
};

// Reverse mapping for API to frontend
export const API_THEME_MAP: Record<ApiTheme, SettingsTheme> = {
  light: "light",
  dark: "dark",
  device_default: "system",
};

// Helper functions
export function mapLanguageToApi(language: Language): ApiLanguage {
  return LANGUAGE_MAP[language];
}

export function mapLanguageFromApi(apiLanguage: ApiLanguage): Language {
  return API_LANGUAGE_MAP[apiLanguage];
}

export function mapThemeToApi(theme: SettingsTheme): ApiTheme {
  return THEME_MAP[theme];
}

export function mapThemeFromApi(apiTheme: ApiTheme): SettingsTheme {
  return API_THEME_MAP[apiTheme];
}

// Get translation key for language (to be used with t() function)
export function getLanguageTranslationKey(language: Language): string {
  const keys: Record<Language, string> = {
    en: "common.language.english",
    fr: "common.language.french",
    de: "common.language.german",
    it: "common.language.italian",
    es: "common.language.spanish",
  };
  return keys[language];
}

// Get translation key for theme (to be used with t() function)
export function getThemeTranslationKey(theme: SettingsTheme): string {
  const keys: Record<SettingsTheme, string> = {
    light: "features.settings.appearance.light",
    dark: "features.settings.appearance.dark",
    system: "features.settings.appearance.system",
  };
  return keys[theme];
}

// Validate and normalize language parameter from URL
export function validateLanguageParam(
  langParam: string | null
): Language | null {
  if (!langParam) {
    return null;
  }

  const normalizedLang = langParam.toLowerCase();
  const supportedLanguages: Language[] = ["en", "fr", "de", "it", "es"];

  const isSupported = supportedLanguages.includes(normalizedLang as Language);

  return isSupported ? (normalizedLang as Language) : null;
}
